/**
 * @file Input 文本输入
 * @description 紧凑单行输入（h-9 / 38px）；支持前后缀与密码显隐；无 size API。
 *
 * @whenToUse 表单文本字段、筛选关键字（也可用 SearchInput）。
 * @whenNotToUse 多行文本（用 Textarea）；下拉选择（用 Select）。
 */

import { useState, type InputHTMLAttributes, type ReactNode } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type InputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'prefix' | 'suffix'
> & {
  /** 校验失败时边框改为 danger */
  invalid?: boolean
  /** 左侧前缀（图标等） */
  prefix?: ReactNode
  /** 右侧后缀 */
  suffix?: ReactNode
  /**
   * `type="password"` 时是否显示显隐切换（小眼睛）。
   * 默认 true；非 password 时忽略。
   */
  showPasswordToggle?: boolean
  /** 包裹容器 class */
  wrapperClassName?: string
}

export function Input({
  invalid = false,
  prefix,
  suffix,
  showPasswordToggle = true,
  className,
  wrapperClassName,
  disabled,
  type,
  ...rest
}: InputProps) {
  const [revealed, setRevealed] = useState(false)
  const isPassword = type === 'password'
  const showToggle = isPassword && showPasswordToggle
  const resolvedType = showToggle && revealed ? 'text' : type

  const toggle = showToggle ? (
    <button
      type="button"
      aria-label={revealed ? '隐藏密码' : '显示密码'}
      disabled={disabled}
      className="inline-flex text-muted transition-opacity duration-150 hover:opacity-90 focus-visible:opacity-90 disabled:pointer-events-none"
      onClick={() => setRevealed((prev) => !prev)}
    >
      {revealed ? (
        <EyeOff className="size-4" aria-hidden />
      ) : (
        <Eye className="size-4" aria-hidden />
      )}
    </button>
  ) : null

  const right =
    suffix || toggle ? (
      <span className="flex shrink-0 items-center gap-2 text-muted [&_svg]:size-4">
        {suffix}
        {toggle}
      </span>
    ) : null

  return (
    <div
      className={cn(
        'flex h-9 w-full items-center gap-2 rounded-lg border-2 bg-raised px-3 transition-opacity duration-200',
        invalid ? 'border-danger' : 'border-border',
        disabled && 'cursor-not-allowed opacity-50',
        wrapperClassName,
      )}
    >
      {prefix ? (
        <span className="shrink-0 text-muted [&_svg]:size-4">{prefix}</span>
      ) : null}
      <input
        type={resolvedType}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-full min-w-0 flex-1 border-0 bg-transparent text-sm text-foreground',
          className,
        )}
        {...rest}
      />
      {right}
    </div>
  )
}
